import React, {useState} from 'react'
import {Menu, MenuItem, SxProps, Theme} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Button, {ButtonProps} from 'src/components-new/primitives/Button'

export interface ActionsMenuItem {
  value: string
  label: string
  disabled?: boolean
}

interface ActionsMenuButtonProps {
  label: React.ReactNode
  items: ActionsMenuItem[]
  onSelect: (value: string) => void
  variant?: ButtonProps['variant']
  startIcon?: React.ReactNode
  disabled?: boolean
  sx?: SxProps<Theme>
}

/**
 * Generic reusable trigger button that opens a dropdown menu of choices, for pages that
 * need a single action button fanning out into several related options (e.g. "pick one of
 * N enabled types, then open a dialog for it") instead of rendering one Button per option.
 * Not tied to any domain — the caller owns what the items mean and what onSelect does with
 * the chosen value. First consumer: ExportsListNew (choosing among PSP-enabled export types).
 */
export default function ActionsMenuButton({
  label,
  items,
  onSelect,
  variant = 'primary',
  startIcon,
  disabled,
  sx,
}: ActionsMenuButtonProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleSelect = (value: string) => {
    handleClose()
    onSelect(value)
  }

  return (
    <>
      <Button
        variant={variant}
        startIcon={startIcon}
        endIcon={<KeyboardArrowDownIcon />}
        disabled={disabled}
        onClick={handleOpen}
        sx={sx}
      >
        {label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{
          elevation: 3,
          sx: {mt: 1, minWidth: 220, borderRadius: 1.5},
        }}
        MenuListProps={{
          sx: {
            py: 0.5,
            '& .MuiMenuItem-root': {minHeight: 45, px: 2, fontSize: 14},
          },
        }}
      >
        {items.map(item => (
          <MenuItem
            key={item.value}
            disabled={item.disabled}
            onClick={() => handleSelect(item.value)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
